import React from "react";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { useTranslation } from "react-i18next";
import {
  COMPOSER_MOBILE_MEDIA_QUERY,
  COMPOSER_ROW_HEIGHT_PX,
} from "./composerConstants";

export const COMPOSE_MODES = {
  CLIENT: "client",
  INTERNAL: "internal",
};

/** Toggle Cliente / Anotação interna — coluna no desktop; faixa horizontal no mobile. */
const ComposeModeBar = ({ mode, onChange, disabled = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const isMobile = useMediaQuery(COMPOSER_MOBILE_MEDIA_QUERY);
  const isInternal = mode === COMPOSE_MODES.INTERNAL;
  const isHorizontal = isMobile;
  const barHeight = isHorizontal ? 32 : COMPOSER_ROW_HEIGHT_PX;

  const clientLabel = t("compose.modeClient", "Responder cliente");
  const internalLabel = t("compose.modeInternal", "Anotação interna");
  const internalHint = t("compose.internalBarHint", "Esta mensagem não será enviada ao cliente");

  return (
    <Box sx={{ flexShrink: 0, alignSelf: "center", display: "flex" }}>
      <ToggleButtonGroup
        exclusive
        orientation={isHorizontal ? "horizontal" : "vertical"}
        size="small"
        value={mode}
        onChange={(_, v) => v && onChange(v)}
        disabled={disabled}
        sx={{
          height: barHeight,
          "& .MuiToggleButtonGroup-grouped": {
            border: `1px solid ${theme.palette.divider}`,
            margin: 0,
            "&:not(:first-of-type)": isHorizontal
              ? { borderLeft: 0, marginLeft: 0 }
              : { borderTop: 0, marginTop: 0 },
          },
          "& .MuiToggleButton-root": {
            flex: 1,
            px: isHorizontal ? 0.75 : 0.75,
            py: 0,
            minWidth: isHorizontal ? 36 : 38,
            borderRadius: 0,
            borderColor: theme.palette.divider,
            color: "text.secondary",
            bgcolor: theme.palette.background.paper,
            "&.Mui-selected": {
              color: theme.palette.primary.contrastText,
              bgcolor: theme.palette.primary.main,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            },
          },
          "& .MuiToggleButton-root:first-of-type": isHorizontal
            ? { borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }
            : { borderTopLeftRadius: 14, borderTopRightRadius: 14 },
          "& .MuiToggleButton-root:last-of-type": {
            ...(isHorizontal
              ? { borderTopRightRadius: 10, borderBottomRightRadius: 10 }
              : { borderBottomLeftRadius: 14, borderBottomRightRadius: 14 }),
            "&.Mui-selected": {
              color: theme.palette.warning.contrastText,
              bgcolor: theme.palette.warning.main,
              "&:hover": { bgcolor: theme.palette.warning.dark },
            },
          },
        }}
      >
        <ToggleButton value={COMPOSE_MODES.CLIENT} aria-label={clientLabel}>
          <Tooltip title={clientLabel} placement={isHorizontal ? "top" : "left"}>
            <ChatOutlinedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value={COMPOSE_MODES.INTERNAL} aria-label={internalLabel}>
          <Tooltip title={isInternal ? internalHint : internalLabel} placement={isHorizontal ? "top" : "left"}>
            <EditNoteOutlinedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ComposeModeBar;
